'use client'
import React, { useState, useEffect, useRef, useMemo } from 'react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { getMapLanguage, getMapRegion } from '@utils'

const shortenPrice = (raw, format) => {
  const n = parseFloat(String(raw ?? '').replace(/[^0-9.]/g, ''))
  if (isNaN(n)) return String(raw ?? '')
  if (format === 'zh') {
    if (n >= 100000000) return (n / 100000000).toFixed(1).replace(/\.0$/, '') + '亿'
    if (n >= 10000)     return (n / 10000).toFixed(1).replace(/\.0$/, '') + '万'
    if (n >= 1000)      return (n / 1000).toFixed(1).replace(/\.0$/, '') + '千'
    return String(n)
  }
  if (format === 'none') return n.toLocaleString()
  if (n >= 1000000000) return (n / 1000000000).toFixed(1).replace(/\.0$/, '') + 'B'
  if (n >= 1000000)    return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
  if (n >= 1000)       return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return String(n)
}

const MARKER_PRESETS = [
  { value: 'pin',    svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40"><path d="M16 0C7.163 0 0 7.163 0 16c0 10 16 24 16 24s16-14 16-24C32 7.163 24.837 0 16 0z" fill="${color}"/><circle cx="16" cy="16" r="6" fill="white"/></svg>` },
  { value: 'circle', svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><circle cx="14" cy="14" r="12" fill="${color}" stroke="white" stroke-width="2.5"/></svg>` },
  { value: 'square', svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28"><rect x="2" y="2" width="24" height="24" rx="4" fill="${color}" stroke="white" stroke-width="2.5"/></svg>` },
  { value: 'star',   svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 20,12 31,12 22,19 25,30 16,23 7,30 10,19 1,12 12,12" fill="${color}" stroke="white" stroke-width="1.5"/></svg>` },
  { value: 'home',   svg: (color) => `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><polygon points="16,2 30,16 25,16 25,30 19,30 19,22 13,22 13,30 7,30 7,16 2,16" fill="${color}" stroke="white" stroke-width="1.5"/></svg>` },
  { value: 'price',  svg: (color, label, opts = {}) => {
    const text = label || '•'; const fs = opts.fontSize ?? 11; const pad = 8
    const W = Math.max(text.length * fs * 0.68, 28) + pad * 2; const H = fs + 10; const tipH = 6
    const totalH = H + tipH; const midX = W / 2; const R = opts.radius ?? H / 2
    const bg = opts.bg ?? color; const tc = opts.textColor ?? '#ffffff'
    const bw = opts.borderWidth ?? 0; const bc = opts.borderColor ?? 'transparent'
    const border = bw > 0 ? `<rect x="${bw/2}" y="${bw/2}" width="${W-bw}" height="${H-bw}" rx="${R}" fill="none" stroke="${bc}" stroke-width="${bw}"/>` : ''
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${totalH}" viewBox="0 0 ${W} ${totalH}"><rect x="0" y="0" width="${W}" height="${H}" rx="${R}" fill="${bg}"/>${border}<polygon points="${midX-5},${H} ${midX+5},${H} ${midX},${totalH}" fill="${bg}"/><text x="${midX}" y="${H/2+fs*0.37}" text-anchor="middle" font-family="Arial,sans-serif" font-size="${fs}" font-weight="700" fill="${tc}">${text}</text></svg>`
  }},
]

const getMarkerSvg = (style, color, w, h, label, markerCfg) => {
  const preset = MARKER_PRESETS.find(p => p.value === style) ?? MARKER_PRESETS[0]
  const pillOpts = style === 'price' ? {
    bg: markerCfg?.pillBg ?? color, textColor: markerCfg?.pillTextColor ?? '#ffffff',
    borderColor: markerCfg?.pillBorderColor ?? 'transparent', borderWidth: markerCfg?.pillBorderWidth ?? 0,
    radius: markerCfg?.pillRadius, fontSize: markerCfg?.pillFontSize ?? 11,
  } : {}
  let svg = preset.svg?.(color, label, pillOpts) ?? MARKER_PRESETS[0].svg(color)
  if (w && h && style !== 'price') {
    svg = svg.replace(/width="[^"]*"/, `width="${w}"`).replace(/height="[^"]*"/, `height="${h}"`)
  }
  return svg
}

const MobileSearchProjectMap = ({ coordinates, hoveredProperty, zoomControl = false, latitude, longitude, dictionary, lang = 'en', mapConfig, onMarkerClick }) => {
  const [markerHighlight, setMarkerHighlight] = useState(hoveredProperty)
  const mapRef           = useRef(null)
  const onMarkerClickRef = useRef(onMarkerClick)
  useEffect(() => { onMarkerClickRef.current = onMarkerClick }, [onMarkerClick])
  useEffect(() => { setMarkerHighlight(hoveredProperty) }, [hoveredProperty])

  useEffect(() => {
    const lat = Number(latitude), lng = Number(longitude)
    if (mapRef.current && isFinite(lat) && isFinite(lng) && lat !== 0 && lng !== 0)
      mapRef.current.panTo({ lat, lng })
  }, [latitude, longitude])

  const { isLoaded } = useJsApiLoader({
    id:               'script-loader',
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API ?? '',
    language:         getMapLanguage(lang),
    region:           getMapRegion(lang),
  })

  const markerStyle = mapConfig?.marker?.style         ?? 'pin'
  const markerColor = mapConfig?.marker?.color         ?? '#3b82f6'
  const markerSize  = mapConfig?.marker?.sizeMobile    ?? mapConfig?.marker?.size ?? 24
  const activeStyle = mapConfig?.markerActive?.style   ?? 'pin'
  const activeColor = mapConfig?.markerActive?.color   ?? '#ef4444'
  const activeSize  = mapConfig?.markerActive?.sizeMobile ?? mapConfig?.markerActive?.size ?? 30

  const makeIcon = (isActive, label) => {
    const style = isActive ? activeStyle : markerStyle
    const color = isActive ? activeColor : markerColor
    const size  = isActive ? activeSize  : markerSize
    const h     = Math.round(size * 1.25)
    const cfg   = isActive ? mapConfig?.markerActive : mapConfig?.marker
    const svg   = getMarkerSvg(style, color, size, h, label, cfg)
    return { url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg) }
  }

  const centerLat = Number(latitude)  || 13.732772
  const centerLng = Number(longitude) || 100.5801953
  const center = useMemo(() => ({ lat: centerLat, lng: centerLng }), [centerLat, centerLng])

  if (!isLoaded) return <div style={{ width: '100%', height: '92vh', background: '#f3f4f6' }} />

  return (
    <div className='z-[49]'>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '92vh', zIndex: 49 }}
        center={center}
        zoom={mapConfig?.zoom ?? 15}
        options={{ disableDefaultUI: true, zoomControl }}
        onLoad={map => { mapRef.current = map }}
      >
        {(coordinates ?? []).map(({ _id, latitude: lat, longitude: lng, salePrice, rentPrice }) => {
          const isActive   = _id === markerHighlight
          const rawPrice   = salePrice || rentPrice || ''
          const fmt        = mapConfig?.marker?.priceShorten === false ? 'none' : 'en'
          const priceLabel = rawPrice ? shortenPrice(rawPrice, fmt) : ''
          return (
            <Marker
              key={_id}
              position={{ lat: Number(lat), lng: Number(lng) }}
              icon={makeIcon(isActive, priceLabel)}
              zIndex={isActive ? 100 : 40}
              onClick={() => onMarkerClickRef.current?.({ _id, latitude: lat, longitude: lng })}
            />
          )
        })}
      </GoogleMap>
    </div>
  )
}

export default MobileSearchProjectMap